import React from 'react';
import { connect } from 'react-redux';
import { submit as submitForm, formValueSelector, getFormValues } from 'redux-form';
import { getCountryCallingCode, parsePhoneNumber } from 'react-phone-number-input';
import { setError, clearError } from '@/actions';
import {
  selectPaymentOption,
  toggleWantsInsurance,
  resetInstallmentsPlan,
  updatePurchaseFields,
  resetPaymentCardError,
  selectPaymentMethod,
  toggleWantsCarbonOffset,
  saveBulkPassengers,
  setPassengers,
  fetchYunoData,
} from '@/actions/purchase';
import { getWalletTypeForPurchase } from 'utils/loyalty';
import { getStoredPassengers } from 'utils/storedPassengers';
import selectIsPurchaserFormValid from 'utils/purchase/selectIsPurchaserFormValid';
import {
  getEnabledBillingCompanyDocuments,
  isBillingCompanyDocumentOrLegacy,
} from 'utils/purchase/billingCompanyDocuments';
import {
  cardPaymentAttempt,
  paymentAttempt,
  transferPaymentAttempt,
  thirdPartyPaymentAttempt,
  storePaymentAttempt,
  efectyPaymentAttempt,
  evertecPaymentAttempt,
  clearOTP,
  generateOTP,
  confirmEvertecPayment,
  yapePaymentAttempt,
  yunoPaymentAttempt,
  bnplPaymentAttempt,
  redeemModalToggle,
  walletPaymentAttempt,
} from '@/actions/payment';
import { paypalPaymentAttempt } from '@/actions/paypal';
import { fetchPaymentCards } from '@/actions/cards';
import availablePaymentEngines from 'constants/PaymentEngine';
import Checkout from './Checkout';
import { getBrand2ProcessorMap } from '../../../utils/documentMaps';
import { CieloTDCProvider } from '../../cielo/CieloTDCContext';
import { useReserhubAccount } from '../../../context/ReserhubAccount';
import taxpayerPayloadFields from '../../../utils/purchase/taxpayerPayloadFields';
import isCdsFrequentPassengerOptInEligible from '../../../utils/isCdsFrequentPassengerOptInEligible';

// Address fields injected at submit time from the purchaser-level phone (see
// `updatePurchaseFields` in actions/purchase.js). They are not rendered by
// AddressFields, so the form always holds `null` for them while the purchase
// holds the stored value — diffing them would never match.
const ADDRESS_DERIVED_FIELDS = ['phone', 'phoneCode', 'phoneCountry'];

export const PURCHASER_ADDRESS_FIELDS = [
  'zipCode',
  'street',
  'number',
  'neighborhood',
  'city',
  'countryCode',
];

/**
 * Compare the six address values controlled by AddressFields.
 * @param {object} params - Address comparison values and feature context.
 * @param {object|null} params.storedAddress - Address echoed by the purchase API.
 * @param {object} params.formAddress - Address currently held by redux-form.
 * @param {string} params.documentType - Purchaser document type.
 * @param {string[]} params.allowedBillingDocuments - Normalized billing-document allowlist.
 * @param {boolean} params.showPurchaserAddressFields - Address-fields feature value.
 * @param {*} params.billingCompanyId - Company resolved by the provider, if any.
 * @returns {boolean} Whether the controllable address values are saved.
 */
export const isPurchaserAddressUpdated = ({
  storedAddress,
  formAddress,
  documentType,
  allowedBillingDocuments,
  showPurchaserAddressFields,
  billingCompanyId,
}) => {
  // A resolved company hides the address section and drops it from the payload, so stale form
  // values must not keep the purchaser dirty forever.
  if (billingCompanyId) return true;
  const usesPurchaserAddress = isBillingCompanyDocumentOrLegacy(
    documentType,
    allowedBillingDocuments,
  );
  if (!showPurchaserAddressFields || !usesPurchaserAddress || !formAddress) return true;
  if (storedAddress === null || storedAddress === undefined) {
    return PURCHASER_ADDRESS_FIELDS.every((field) => !formAddress[field]);
  }

  return PURCHASER_ADDRESS_FIELDS.every((field) => storedAddress[field] === formAddress[field]);
};

/**
 * Compare a billing-company scalar only while its UI experience is active.
 * @param {object} params - Company comparison values and effective UI context.
 * @param {*} params.storedValue - Value echoed by the purchase API.
 * @param {*} params.formValue - Current redux-form value.
 * @param {boolean} params.billingCompanyValidationEnabled - Effective company UI marker.
 * @returns {boolean} Whether the scalar is saved or intentionally ignored.
 */
export const isBillingCompanyFieldUpdated = ({
  storedValue,
  formValue,
  billingCompanyValidationEnabled,
}) => {
  const storedValueIsEmpty =
    storedValue === undefined || storedValue === null || storedValue === '';
  const formValueIsEmpty = formValue === undefined || formValue === null || formValue === '';

  // The API can omit an empty scalar, echo it as null, or serialize it as an empty string. They
  // all represent the same saved value and must not leave the purchaser dirty after the PUT.
  if (storedValueIsEmpty && formValueIsEmpty) return true;

  if (billingCompanyValidationEnabled) {
    return storedValue === formValue;
  }
  return storedValue === undefined || storedValue === formValue;
};

export const mapStateToProps = (state) => {
  const {
    purchase,
    payment,
    siemprePlus,
    costapass,
    cards,
    whitelabelConfig: { features, env },
    reserhubAccount,
    cdsFrequentPassenger,
  } = state;

  const VALIDATE_PURCHASER = features.PURCHASER_FORM_ON === 'CHECKOUT';
  const CHECK_DOCUMENT = features.IDENTIFICATION_DOCUMENT_FOR_PURCHASER;
  const allowedBillingDocuments = getEnabledBillingCompanyDocuments(
    features.BILLING_COMPANY_ENABLED,
    features.BILLING_COMPANY_AVAILABLE_DOCUMENTS,
  );
  const billingCompanyFlowConfigured = allowedBillingDocuments.length > 0;
  const showCdsFrequentPassengerOptIn = isCdsFrequentPassengerOptInEligible({
    features,
    brand: env.brand,
    cdsSession: {
      isBootstrapped: cdsFrequentPassenger?.get('isBootstrapped'),
      isAuthenticated: cdsFrequentPassenger?.get('isAuthenticated'),
      error: cdsFrequentPassenger?.get('error'),
      usesReserhubAccount: cdsFrequentPassenger?.get('usesReserhubAccount'),
    },
    reserhubUser: reserhubAccount?.get('user'),
    isReserhubUserLoading: reserhubAccount?.get('isUserLoading'),
    isReserhubUserFetched: reserhubAccount?.get('isUserFetched'),
    isReserhubUserAnonymous: reserhubAccount?.get('isUserAnonymous'),
  });

  const keys = [
    'purchaserFirstName',
    'purchaserLastName',
    features.SHOW_PURCHASER_SECOND_LAST_NAME_FIELD && 'purchaserSecondLastName',
    'email',
    'phoneCountry',
    'phone',
    'phoneCode',
    CHECK_DOCUMENT && 'documentType',
    CHECK_DOCUMENT && 'documentId',
    'taxpayerId',
    'taxpayerLegalName',
    'taxpayerAddress',
    'billingCompanyId',
    'billingCompanyCityId',
    'billingCompanyValidationEnabled',
    'addressAttributes',
    features.SHOW_PROMOTIONAL_TERMS_CHECKBOX && 'promotionalTerms',
    showCdsFrequentPassengerOptIn && 'cdsFrequentPassengerOptIn',
  ].filter(Boolean);

  const purchaseJs = purchase.toJS();
  const cardsJs = cards.toJS();
  const userData = siemprePlus.toJS().user;

  const cardList = cards.get('cards');
  const selectedCardReference = cards.get('selectedCard');

  const selectedCard = cardList.find(
    (card) => card.recurringDetail.recurringDetailReference === selectedCardReference,
  );

  const cardSelector = formValueSelector('card');
  const purchaserFields = formValueSelector('purchaser')(state, ...keys);
  // Invoice recipient (self / organization) is set via redux-form `change`, not a
  // permanent Field, so it is not in `keys`. Select it separately and merge it into
  // the submitted purchaser without affecting the `keys`-based purchaserUpdated check.
  const billingRecipientType = formValueSelector('purchaser')(state, 'billingRecipientType');
  // PRO2-128: the invoice toggle carries a withdrawal the taxpayer fields cannot express — clearing
  // them deletes the keys, which is indistinguishable from never having asked. It is selected here
  // for the same reason as the recipient above and must stay out of `keys` for a sharper one: the
  // backend never echoes it, so a `keys`-based comparison could never converge and `purchaserUpdated`
  // would stay false forever once the buyer touched the toggle.
  const invoice = formValueSelector('purchaser')(state, 'invoice');
  const passengersFormValues = getFormValues('passengers')(state);
  const storedFirstPassenger = getStoredPassengers(features)[0];
  // Passengers form is destroyed on route change; persisted form values keep full PII for analytics.
  const firstPassengerProfile =
    passengersFormValues?.passengers?.[0] ||
    storedFirstPassenger ||
    purchaseJs.passengers?.[0] ||
    null;
  const legalTerms = formValueSelector('purchaser')(state, 'legalTerms');
  const requiresTermsValidation =
    features.POLICIES_DISCLAIMER_LOCATION === 'CHECKOUT' && features.SHOW_LEGAL_TERMS_CHECKBOX;

  if (purchaserFields.phone) {
    purchaserFields.phone = purchaserFields.phone.replace(/ /g, '');
  }

  const purchaserFieldMatches = (key) => {
    if (purchaserFields[key] === undefined) return true;
    // phoneCode is derived from phoneCountry; compare only the source field
    if (key === 'phoneCode') return true;
    // Server-owned/write-only company state must not wait for a purchase echo to become clean.
    if (
      ['billingCompanyId', 'billingCompanyCityId', 'billingCompanyValidationEnabled'].includes(key)
    )
      return true;
    if (key === 'email' && !features.SHOW_PURCHASER_EMAIL_FIELD) return true;
    if (key === 'phone') {
      const phoneCode =
        purchaserFields.phoneCountry && getCountryCallingCode(purchaserFields.phoneCountry);
      const parsedNumber = parsePhoneNumber(`+${phoneCode}${purchaserFields.phone}`);
      const phone = parsedNumber && parsedNumber.nationalNumber;
      return purchaseJs[key] === phone;
    }

    if (['taxpayerLegalName', 'taxpayerAddress'].includes(key)) {
      return isBillingCompanyFieldUpdated({
        storedValue: purchaseJs[key],
        formValue: purchaserFields[key],
        billingCompanyValidationEnabled: purchaserFields.billingCompanyValidationEnabled,
      });
    }

    if (key === 'promotionalTerms') {
      const stored = purchaseJs.meta?.accepted_promotional_terms;
      const formChecked = Boolean(purchaserFields.promotionalTerms);
      return (stored === 'true') === formChecked;
    }

    if (key === 'cdsFrequentPassengerOptIn') {
      const stored = purchaseJs.meta?.frequent_passenger_enrollment;
      const formChecked = Boolean(purchaserFields.cdsFrequentPassengerOptIn);
      return (stored === 'true') === formChecked;
    }

    if (key === 'addressAttributes') {
      if (!billingCompanyFlowConfigured) {
        return Object.keys(purchaserFields[key])
          .filter((field) => !ADDRESS_DERIVED_FIELDS.includes(field))
          .every((field) => purchaseJs[key]?.[field] === purchaserFields[key][field]);
      }

      // No derived-field filter needed here: `isPurchaserAddressUpdated` compares only
      // PURCHASER_ADDRESS_FIELDS, which already excludes the injected phone values.
      return isPurchaserAddressUpdated({
        storedAddress: purchaseJs[key],
        formAddress: purchaserFields[key],
        documentType: purchaserFields.documentType,
        allowedBillingDocuments,
        showPurchaserAddressFields: features.SHOW_PURCHASER_ADDRESS_FIELDS,
        billingCompanyId: purchaserFields.billingCompanyId,
      });
    }

    return purchaseJs[key] === purchaserFields[key];
  };

  // billingRecipientType lives outside `keys` (set via redux-form `change`), so a
  // recipient-only change (e.g. selecting "self") would otherwise leave
  // purchaserUpdated true and skip updatePurchaseFields, dropping
  // billing_recipient_type from the request. Compare it against the stored value.
  // Normalize both sides: "no invoice" clears the form value to '' while the purchase
  // reports the absent recipient as null/undefined.
  const billingRecipientMatches =
    billingRecipientType === undefined ||
    (purchaseJs.billingRecipientType || '') === (billingRecipientType || '');
  // PRO2-128: a withdrawal has to make the form dirty or the update is skipped and the backend keeps
  // the taxpayer data — the same trap the recipient comment above describes. It cannot be compared
  // against `purchaseJs.invoice`, which never exists, so compare what the withdrawal actually
  // changes: the purchase is stale while the buyer has turned the invoice off and it still carries a
  // taxpayer id, and clean again once the clear has landed. Turning the toggle *on* needs nothing
  // here — `taxpayerId` is already in `keys` and goes dirty as soon as the buyer types one.
  // Keyed on what `updatePurchaseFields` will actually write, by asking the same module it writes
  // from rather than restating its condition here. Restating is what let the check and the write
  // drift apart repeatedly in this change; deriving cannot. It matters because that writer stands
  // down for the billing-company flow, which owns these fields for another purpose: pending a write
  // that never happens leaves the stored id in place, keeps this true forever, and blocks payment on
  // a valid form. See docs/solutions/conventions/a-list-with-two-consumers-is-a-shared-function.md.
  const pendingTaxpayerWrite = taxpayerPayloadFields({ ...purchaserFields, invoice });
  const invoiceWithdrawalPending =
    pendingTaxpayerWrite.taxpayerId === '' && Boolean(purchaseJs.taxpayerId);
  const purchaserUpdated =
    keys.every(purchaserFieldMatches) && billingRecipientMatches && !invoiceWithdrawalPending;

  const isPurchaserValid = purchaserUpdated && (!requiresTermsValidation || legalTerms);
  // Registration-independent validity (see selectIsPurchaserFormValid); used to gate wallet sheets.
  // Gated on VALIDATE_PURCHASER so it cannot report `false` (and block wallets) when purchaser
  // validation is bypassed for the tenant, matching `purchaserUpdated` below.
  const isPurchaserFormValid = VALIDATE_PURCHASER ? selectIsPurchaserFormValid(state) : true;

  const { availableWallets = [] } = purchaseJs;
  const selectedLoyaltyProgram = purchaseJs.walletType || getWalletTypeForPurchase();
  const isValidWalletType =
    selectedLoyaltyProgram && availableWallets.includes(selectedLoyaltyProgram);

  // Show WalletNotAvailable when user is logged in with costapass/travelpass but wallet is not available for route
  const showWalletNotAvailable =
    costapass.get('logged') &&
    !availableWallets.includes('costapass') &&
    !availableWallets.includes('travelpass');

  return {
    user: userData,
    isLogged: siemprePlus.get('logged') || costapass.get('logged'),
    showCdsFrequentPassengerOptIn,
    showWalletNotAvailable,
    cardsUpdated: cardsJs.isUpdated,
    cardsLoading: cardsJs.isLoading,
    selectedCard,
    purchase: purchaseJs,
    isUpdating: purchase.get('isUpdating'),
    isExchange: purchase.get('isExchange'),
    fetchingPayment: payment.get('isFetching') || false,
    stripeSubmitting: payment.get('stripeSubmitting') || false,
    paymentId: payment.get('id'),
    cardErrorOccured: payment.get('cardErrorOccured'),
    purchaserUpdated: VALIDATE_PURCHASER ? isPurchaserValid : true,
    isPurchaserFormValid,
    // Forward when defined, not merely truthy: deselecting the recipient clears
    // it to '' and that clear has to flow into updatePurchaseFields so the backend
    // can drop the previous recipient. `undefined` (never set) is still omitted.
    purchaser: {
      ...purchaserFields,
      ...(billingRecipientType !== undefined && { billingRecipientType }),
      // Same rule for the invoice marker: `false` is the withdrawal and must travel, `undefined`
      // (never touched) stays omitted so the payload is unchanged for everyone else.
      ...(invoice !== undefined && { invoice }),
    },
    firstPassengerProfile,
    cardValues: cardSelector(
      state,
      'holderName',
      'cardNumber',
      'cvv2',
      'expirationMonth',
      'expirationYear',
      'expirationDate',
      'paymentPlan',
      'monthlySelectedPlan',
      'billingAddress',
      'cardBrand',
    ),
    insuranceMessage: purchase.getIn(['config', 'insuranceMessage']),
    insuranceAmount: purchase.getIn(['config', 'insuranceAmount']),
    insuranceAmountWithTaxes: purchase.getIn(['config', 'insuranceAmountWithTaxes']),
    insuranceAmountIsUnitPrice: purchase.getIn(['config', 'unitAmount']),
    selectedPlan: purchase.get('selectedPlan'),
    paymentEngine: purchase.get('paymentEngine'),
    monthlySelectedPlan: purchase.get('monthlySelectedPlan'),
    selectedPaymentMethod: purchase.get('selectedPaymentMethod'),

    /**
     * Carbon ancillary props
     */
    carbonOffset: purchase.getIn(['config', 'carbonOffset'])?.toJS(),
    showRedeemModal: payment.get('showRedeemModal'),
    loadingOTP: payment.get('loadingOTP'),
    showOTPModal: payment.get('showOTPModal'),
    otpConfirmed: payment.get('otpConfirmed'),
    otpTriggerPayment: payment.get('otpTriggerPayment'),
    nitCodeValidated: payment.get('nitCodeValidated'),
    selectedLoyaltyProgram: isValidWalletType ? selectedLoyaltyProgram : null,
    isUserAuthenticated: !!(userData || reserhubAccount?.toJS()?.user),
    features,
    enableThreeDS: env.cielo?.enableThreeDS,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  fetchCardList: () => dispatch(fetchPaymentCards()),
  onToggleWantsInsurance: (token, wants) => dispatch(toggleWantsInsurance(token, wants)),
  onSubmitForm: (formName) => dispatch(submitForm(formName)),
  onFormFail: () =>
    dispatch(
      setError(null, 'check_the_fields_in_red', 'warning', false, null, null, null, 'fixed'),
    ),
  onNoCardSelected: () =>
    dispatch(setError(200, 'select_a_card', 'warning', false, null, null, 'fixed')),
  onPaymentAttempt: (token, type) => dispatch(paymentAttempt(token, type)),
  onThirdpartyPaymentAttempt: (token, type) => dispatch(thirdPartyPaymentAttempt(token, type)),
  onSelectPaymentOption: (option, method) => {
    dispatch(selectPaymentOption(option));
    dispatch(selectPaymentMethod(method || {}));
  },
  transferPaymentAttempt: (config) => dispatch(transferPaymentAttempt(config)),
  resetInstallmentsPlan: () => {
    dispatch(resetInstallmentsPlan());
  },
  storePaymentAttempt: (config) => dispatch(storePaymentAttempt(config)),
  transitionTo: (route) => ownProps.history.push(route),
  returnTo: (route) => ownProps.history.replace(route),
  setPurchaser: (token, fields) => dispatch(updatePurchaseFields(token, fields)),
  resetPaymentCardError: () => dispatch(resetPaymentCardError()),
  onEfectyPaymentAttempt: () => dispatch(efectyPaymentAttempt()),
  onBNPLPaymentAttempt: () => dispatch(bnplPaymentAttempt()),
  onWalletPaymentAttempt: () => dispatch(walletPaymentAttempt()),
  yapePaymentAttempt: (yapeFormData) => dispatch(yapePaymentAttempt(yapeFormData)),
  onEvertecPaymentAttempt: (paymentData) => dispatch(evertecPaymentAttempt(paymentData)),
  onConfirmEvertecPayment: () => dispatch(confirmEvertecPayment()),
  onEvertecPaymentFailure: () =>
    dispatch(setError(301, 'error_generating_payment', 'warning', false)),
  onYunoPaymentAttempt: (paymentData) => dispatch(yunoPaymentAttempt(paymentData)),
  fetchYunoData: (purchaseData) => dispatch(fetchYunoData(purchaseData)),
  clearError: () => dispatch(clearError()),
  /**
   * Carbon ancillary props
   */
  onToggleWantsCarbonOffset: (token, wants) => dispatch(toggleWantsCarbonOffset(token, wants)),
  clearOTP: () => dispatch(clearOTP()),
  generateOTP: (walletBalanceUsed) => dispatch(generateOTP(walletBalanceUsed)),
  dispatchCardPaymentAttempt: (token, cardValues, paymentInfo) =>
    dispatch(cardPaymentAttempt(token, cardValues, paymentInfo)),
  saveBulkPassengers: (token, passengerPayload) =>
    dispatch(saveBulkPassengers(token, passengerPayload)),
  savePassengers: (token, passengers) => dispatch(setPassengers(passengers, token)),
  redeemModalToggle: (show) => dispatch(redeemModalToggle(show)),
  onPaypalPaymentAttempt: (paymentType, options) =>
    dispatch(paypalPaymentAttempt(paymentType, options)),
});

export const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  onCardPaymentAttempt: (token, cardValues, paymentInfo) => {
    const { dispatchCardPaymentAttempt } = dispatchProps;
    const { monthlySelectedPlan } = stateProps.purchase;
    const cardValueWithMonthlyPlan = {
      ...cardValues,
      monthlySelectedPlan,
    };
    dispatchCardPaymentAttempt(token, cardValueWithMonthlyPlan, paymentInfo);
  },
  onTransferPaymentAttempt: ({ bank, personType, isLegacyPix, addressAttributes }) => {
    const {
      description,
      documentId,
      documentType,
      email,
      selectedInstallmentsPlan,
      token,
      total,
      selectedPaymentMethod,
      phone,
      phoneCode,
      phoneCountry,
    } = stateProps.purchase;
    const { transferPaymentAttempt } = dispatchProps;
    const { engine, provider } = selectedPaymentMethod;
    const isPSEMercadoPago = engine === 'mercadopago' && provider === 'pse';

    const validatedTotal =
      selectedInstallmentsPlan && selectedInstallmentsPlan.months > 1
        ? selectedInstallmentsPlan.total
        : total;

    const brand2ProcessorMap = getBrand2ProcessorMap();
    transferPaymentAttempt({
      bankId: bank,
      personType,
      documentType: isPSEMercadoPago ? brand2ProcessorMap[documentType] : documentType,
      documentNumber: documentId,
      email,
      paymentDescription: description,
      purchaseToken: token,
      total: validatedTotal,
      engine: isLegacyPix ? availablePaymentEngines.mercadoPago : selectedPaymentMethod.engine,
      provider: isLegacyPix ? 'pix' : selectedPaymentMethod.provider,
      addressAttributes: isPSEMercadoPago
        ? {
            ...addressAttributes,
            phone,
            phoneCode,
            phoneCountry,
          }
        : {},
    });
  },
  onSetPurchaser: () => {
    const { setPurchaser } = dispatchProps;
    const { purchase, purchaser } = stateProps;

    return setPurchaser(purchase.token, purchaser);
  },
  onStorePaymentAttempt: () => {
    const {
      token,
      purchaserFirstName,
      purchaserLastName,
      documentId,
      documentType,
      selectedInstallmentsPlan,
      description,
      total,
    } = stateProps.purchase;
    const { storePaymentAttempt } = dispatchProps;

    const validatedTotal =
      selectedInstallmentsPlan && selectedInstallmentsPlan.months > 1
        ? selectedInstallmentsPlan.total
        : total;

    const brand2ProcessorMap = getBrand2ProcessorMap();
    storePaymentAttempt({
      token,
      documentType: brand2ProcessorMap[documentType],
      name: purchaserFirstName,
      lastName: purchaserLastName,
      identification: documentId,
      totalAmount: validatedTotal,
      currency: 'COP',
      description,
    });
  },

  // TODO: Review if this work for saved cards
  onAdyenPaymentAttempt: (selectedCard) => {
    const { token: purchaseToken, total, monthlySelectedPlan } = stateProps.purchase;
    const { currency } = stateProps.env;
    const { recurringDetail } = selectedCard;

    // This object don't have adyenToken because is not a new card
    // and only need the reference to make the payment
    const paymentInfo = {
      amount: total,
      reference: recurringDetail.recurringDetailReference,
      currency,
    };

    const cardValues = {
      holderName: recurringDetail.card.holderName,
      cardNumber: `${recurringDetail.additionalData.cardBin}xxxxxx${recurringDetail.card.number}`,
      cardBrand: recurringDetail.variant,
      monthlySelectedPlan,
    };

    cardPaymentAttempt(purchaseToken, cardValues, paymentInfo);
  },
});

const CheckoutContainer = connect(mapStateToProps, mapDispatchToProps, mergeProps)(Checkout);

const CheckoutWithPaymentProviders = (routerProps) => {
  // Reserhub unified account suppresses the Doters banner (LoyaltyWidget returns null when enabled).
  // Pass it down so the "additional services" gate doesn't render an empty section title.
  const { enabled: reserhubAccountEnabled } = useReserhubAccount();

  return (
    <CieloTDCProvider>
      <CheckoutContainer {...routerProps} reserhubAccountEnabled={reserhubAccountEnabled} />
    </CieloTDCProvider>
  );
};

export default CheckoutWithPaymentProviders;
